// CollectionChat - Group chat component for collections
// Feature: Collection chat with system messages for sales

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSignInModalStore } from '../../store/signInModalStore';
import { formatDistanceToNow } from 'date-fns';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import {
  useCollectionChatMessages,
  useCollectionChatOlderMessages,
  useCollectionChatPolling,
  useSendCollectionChatMessage,
  useMarkCollectionChatAsRead,
  useCollectionChatRateLimit,
  type CollectionMessage,
  parseSystemMessage,
  shortenAddress,
} from '../../hooks/useCollectionChat';
import { formatPrice } from '../../utils/formatters';

interface CollectionChatProps {
  collectionId: string;
  isActive: boolean; // Whether the chat tab is currently active
  isHeaderCollapsed?: boolean; // Whether the collection header is collapsed (for dynamic height)
}

// Format wallet address for display
function formatAddress(address: string, length = 6): string {
  if (!address) return 'Unknown';
  if (address.length <= length * 2) return address;
  return `${address.slice(0, length)}...${address.slice(-4)}`;
}

// Get display name or formatted address
function getDisplayName(address: string | null, displayName: string | null): string {
  if (!address) return 'System';
  return displayName || formatAddress(address);
}

// Safe date formatting with fallback
function formatMessageTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return '';
  }
}

export function CollectionChat({ collectionId, isActive, isHeaderCollapsed = false }: CollectionChatProps) {
  const { isAuthenticated, user } = useAuthStore();
  const { error: showError } = useNotificationStore();
  const openSignIn = useSignInModalStore((st) => st.open);

  // Message input state
  const [messageContent, setMessageContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const prevMessageCountRef = useRef<number>(0);
  const isLoadingOlderRef = useRef(false);
  const scrollPositionRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);

  // Fetch messages
  const {
    data: messagesData,
    isLoading: loadingMessages,
    refetch: refetchMessages,
  } = useCollectionChatMessages(collectionId, { enabled: isActive });

  const messages = messagesData?.messages || [];
  const initialHasMore = messagesData?.hasMore ?? false;

  // Older messages for infinite scroll
  const {
    loadOlderMessages,
    isLoading: loadingOlder,
    hasMore: olderHasMore,
  } = useCollectionChatOlderMessages(collectionId, messages);

  // Use initial hasMore until we've loaded older messages, then use the hook's hasMore
  const [hasLoadedOlder, setHasLoadedOlder] = useState(false);
  const hasMore = hasLoadedOlder ? olderHasMore : initialHasMore;

  // Track all messages including older ones
  const [allMessages, setAllMessages] = useState<CollectionMessage[]>([]);

  // Update allMessages when base messages change
  useEffect(() => {
    if (messages.length > 0) {
      setAllMessages(prev => {
        // If we have older messages prepended, keep them and update the newer ones
        if (prev.length > 0 && prev[0].created_at < messages[0].created_at) {
          // Find where old messages end and new messages begin
          const lastOldTimestamp = prev.find(m => m.created_at >= messages[0].created_at);
          if (lastOldTimestamp) {
            // Merge: keep older messages that aren't in the new set
            const olderMessages = prev.filter(m => m.created_at < messages[0].created_at);
            return [...olderMessages, ...messages];
          }
        }
        return messages;
      });
    }
  }, [messages]);

  // Real-time polling (only when tab is active)
  const { initializeFromMessages, hasNewMessages, newMessages } = useCollectionChatPolling(
    collectionId,
    { pollingInterval: 2000, enabled: isActive }
  );

  // Initialize polling timestamp when messages load
  useEffect(() => {
    if (allMessages.length > 0) {
      initializeFromMessages(allMessages);
    }
  }, [allMessages.length > 0, initializeFromMessages]);

  // Append new messages from polling
  useEffect(() => {
    if (hasNewMessages && newMessages.length > 0) {
      setAllMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const trulyNew = newMessages.filter(m => !existingIds.has(m.id));
        if (trulyNew.length > 0) {
          return [...prev, ...trulyNew];
        }
        return prev;
      });
    }
  }, [hasNewMessages, newMessages]);

  // Rate limit tracking
  const {
    isLimited,
    secondsRemaining,
    rateLimitSeconds,
    refetch: refetchRateLimit,
  } = useCollectionChatRateLimit(collectionId);

  // Mutations
  const sendMessageMutation = useSendCollectionChatMessage();
  const markAsReadMutation = useMarkCollectionChatAsRead();

  // Mark as read when becoming active
  useEffect(() => {
    if (isActive && isAuthenticated && collectionId) {
      markAsReadMutation.mutate(collectionId);
    }
  }, [isActive, isAuthenticated, collectionId]);

  // Scroll to bottom helper
  const scrollToBottom = useCallback((smooth = true) => {
    const container = messagesContainerRef.current;
    if (container) {
      if (smooth) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, []);

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    if (allMessages.length > 0 && !isLoadingOlderRef.current) {
      const currentCount = allMessages.length;
      const isInitialLoad = prevMessageCountRef.current === 0;
      const hasNewMsgs = currentCount > prevMessageCountRef.current;

      if (isInitialLoad) {
        // Initial load - scroll immediately without animation
        setTimeout(() => scrollToBottom(false), 50);
      } else if (hasNewMsgs && !scrollPositionRef.current) {
        // New message arrived - smooth scroll (but not if we just loaded older)
        scrollToBottom(true);
      }
      prevMessageCountRef.current = currentCount;
    }
  }, [allMessages, scrollToBottom]);

  // Restore scroll position after loading older messages
  useEffect(() => {
    if (scrollPositionRef.current && !loadingOlder) {
      const container = messagesContainerRef.current;
      if (container) {
        const newScrollHeight = container.scrollHeight;
        const scrollDiff = newScrollHeight - scrollPositionRef.current.scrollHeight;
        container.scrollTop = scrollPositionRef.current.scrollTop + scrollDiff;
        scrollPositionRef.current = null;
      }
    }
  }, [loadingOlder, allMessages]);

  // Handle scroll to load older messages
  const handleScroll = useCallback(async (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    // Load more when scrolled near the top
    if (container.scrollTop < 100 && hasMore && !loadingOlder && !isLoadingOlderRef.current) {
      isLoadingOlderRef.current = true;
      // Save scroll position
      scrollPositionRef.current = {
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
      };

      try {
        const olderMsgs = await loadOlderMessages();
        setHasLoadedOlder(true); // Switch to using hook's hasMore after first load
        if (olderMsgs.length > 0) {
          setAllMessages(prev => [...olderMsgs, ...prev]);
        }
      } finally {
        isLoadingOlderRef.current = false;
      }
    }
  }, [hasMore, loadingOlder, loadOlderMessages]);

  // Send message
  const handleSendMessage = async () => {
    if (!messageContent.trim() || !collectionId || isSending || !isAuthenticated) return;

    if (isLimited) {
      showError(`Please wait ${secondsRemaining} seconds before posting again`);
      return;
    }

    setIsSending(true);

    try {
      const newMessage = await sendMessageMutation.mutateAsync({
        collectionId,
        content: messageContent.trim(),
      });
      setMessageContent('');
      messageInputRef.current?.focus();
      // Add the new message immediately
      setAllMessages(prev => [...prev, newMessage]);
      // Refetch rate limit status
      refetchRateLimit();
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(true), 100);
    } catch (error) {
      showError((error as Error).message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Handle Enter key to send
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Render a system message (sale notification)
  const renderSystemMessage = (message: CollectionMessage) => {
    const parsed = parseSystemMessage(message.content);
    if (!parsed) {
      // Fallback to raw content if parsing fails
      return (
        <div className="text-center py-2">
          <span className="text-caption font-caption text-subtext-color bg-neutral-100/50 px-3 py-1 rounded-full">
            {message.content}
          </span>
        </div>
      );
    }

    return (
      <div className="text-center py-2 px-2">
        <div className="inline-flex flex-wrap items-center justify-center gap-1 sm:gap-2 text-caption font-caption text-subtext-color bg-neutral-100/50 px-3 py-1.5 rounded-xl max-w-full">
          <span className="text-sm shrink-0">Sale</span>
          <Link
            to={`/nfts/${parsed.nftTokenId}`}
            className="text-brand-primary hover:underline font-medium truncate max-w-[120px] sm:max-w-[200px]"
            title={parsed.nftName}
          >
            {parsed.nftName}
          </Link>
          <span className="shrink-0">for</span>
          <span className="font-medium text-default-font shrink-0">{formatPrice(parsed.priceXRP)} XRP</span>
          <span className="text-neutral-400 hidden sm:inline">|</span>
          <span className="w-full sm:hidden" /> {/* Line break on mobile */}
          <Link
            to={`/wallets/${parsed.sellerAddress}`}
            className="text-brand-primary hover:underline shrink-0"
          >
            {shortenAddress(parsed.sellerAddress)}
          </Link>
          <span className="shrink-0">&rarr;</span>
          <Link
            to={`/wallets/${parsed.buyerAddress}`}
            className="text-brand-primary hover:underline shrink-0"
          >
            {shortenAddress(parsed.buyerAddress)}
          </Link>
        </div>
      </div>
    );
  };

  // Render a user message
  const renderUserMessage = (message: CollectionMessage, index: number, arr: CollectionMessage[]) => {
    const isOwnMessage = message.sender_address === user?.xrpl_address;
    const prevMessage = index > 0 ? arr[index - 1] : null;
    const nextMessage = index < arr.length - 1 ? arr[index + 1] : null;

    // Group messages from same sender (unless system messages in between)
    const isFirstInGroup =
      !prevMessage ||
      prevMessage.sender_address !== message.sender_address ||
      prevMessage.is_system_message;
    const isLastInGroup =
      !nextMessage ||
      nextMessage.sender_address !== message.sender_address ||
      nextMessage.is_system_message;

    // Get display name - "You" for own messages, otherwise normal display name
    const displayName = isOwnMessage
      ? 'You'
      : getDisplayName(message.sender_address, message.sender_display_name);

    return (
      <div
        className={`flex justify-start ${!isFirstInGroup ? 'mt-0.5' : 'mt-2'}`}
      >
        {/* Avatar for first message in group - hidden on mobile for more width */}
        {isFirstInGroup && (
          isOwnMessage ? (
            <div className="flex-shrink-0 mr-2 hidden sm:block">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-primary to-brand-600 flex items-center justify-center">
                <span className="text-white font-medium text-xs">Y</span>
              </div>
            </div>
          ) : (
            <Link
              to={`/wallets/${message.sender_address}`}
              className="flex-shrink-0 mr-2 hidden sm:block"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-neutral-600 to-neutral-700 flex items-center justify-center">
                <span className="text-white font-medium text-xs">
                  {(message.sender_display_name || message.sender_address || 'U')[0].toUpperCase()}
                </span>
              </div>
            </Link>
          )
        )}

        {/* Spacer for non-first messages - hidden on mobile */}
        {!isFirstInGroup && <div className="w-8 mr-2 flex-shrink-0 hidden sm:block" />}

        <div className="flex flex-col max-w-[95%] sm:max-w-[90%]">
          {/* Sender name for first message in group */}
          {isFirstInGroup && (
            isOwnMessage ? (
              <span className="text-caption font-caption text-subtext-color mb-0.5 ml-1">
                {displayName}
              </span>
            ) : (
              <Link
                to={`/wallets/${message.sender_address}`}
                className="text-caption font-caption text-subtext-color hover:text-brand-primary mb-0.5 ml-1"
              >
                {displayName}
              </Link>
            )
          )}

          <div
            className={`relative px-3 py-2 shadow-sm ${
              isOwnMessage
                ? 'bg-gradient-to-br from-brand-primary to-brand-primary/90 text-white'
                : 'bg-neutral-700 text-neutral-100'
            } ${
              // Bubble shape based on position in group (all left-aligned now)
              isFirstInGroup && isLastInGroup
                ? 'rounded-2xl rounded-bl-md'
                : isFirstInGroup
                ? 'rounded-2xl rounded-bl-md'
                : isLastInGroup
                ? 'rounded-2xl rounded-tl-md rounded-bl-md'
                : 'rounded-2xl rounded-l-md'
            }`}
          >
            <p className="text-body font-body whitespace-pre-wrap break-words leading-relaxed text-xs sm:text-sm">
              {message.content}
            </p>
            {isLastInGroup && (
              <p
                className={`text-[10px] mt-1 text-right ${
                  isOwnMessage ? 'text-white/60' : 'text-neutral-400'
                }`}
              >
                {formatMessageTime(message.created_at)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Dynamic height based on header state
  // Uses calc to fill available viewport height minus header/nav/padding
  // When header is collapsed: ~270px for nav + collapsed header + tabs + padding + bottom margin
  // When header is expanded: ~495px for nav + full header + tabs + padding + bottom margin
  const heightClass = isHeaderCollapsed
    ? 'h-[calc(100vh-270px)] min-h-[300px]'
    : 'h-[calc(100vh-495px)] min-h-[250px]';

  return (
    <div className={`flex flex-col ${heightClass} bg-default-background border border-neutral-border rounded-xl overflow-hidden`}>
      {/* Messages container */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1"
      >
        {/* Loading older indicator */}
        {loadingOlder && (
          <div className="flex items-center justify-center py-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-primary border-t-transparent"></div>
            <span className="ml-2 text-caption text-subtext-color">Loading older messages...</span>
          </div>
        )}

        {/* Has more indicator */}
        {hasMore && !loadingOlder && allMessages.length > 0 && (
          <div className="text-center py-2">
            <span className="text-caption text-subtext-color">Scroll up for older messages</span>
          </div>
        )}

        {loadingMessages && allMessages.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        ) : allMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <svg
              className="h-12 w-12 text-subtext-color mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p className="text-body-bold font-body-bold text-default-font">No messages yet</p>
            <p className="text-body font-body text-subtext-color mt-1">
              Be the first to start the conversation!
            </p>
          </div>
        ) : (
          allMessages.map((message, index, arr) =>
            message.is_system_message ? (
              <div key={message.id}>{renderSystemMessage(message)}</div>
            ) : (
              <div key={message.id}>{renderUserMessage(message, index, arr)}</div>
            )
          )
        )}
      </div>

      {/* Message input */}
      <div className="flex-shrink-0 p-3 border-t border-neutral-border bg-neutral-50/50">
        {isAuthenticated ? (
          <>
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={messageInputRef}
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  maxLength={500}
                  rows={1}
                  className="w-full min-h-[40px] max-h-[80px] px-3 py-2 text-sm rounded-xl border border-neutral-border bg-default-background text-default-font placeholder-subtext-color focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/20 resize-none transition-all"
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = `${Math.min(target.scrollHeight, 80)}px`;
                  }}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!messageContent.trim() || isSending || isLimited}
                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                  messageContent.trim() && !isSending && !isLimited
                    ? 'bg-brand-primary hover:bg-brand-primary/90 text-white shadow-md hover:shadow-lg'
                    : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                }`}
              >
                {isSending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                )}
              </button>
            </div>

            {/* Rate limit countdown and char count */}
            <div className="flex items-center justify-between mt-1.5 px-1">
              <div className="text-[11px] text-subtext-color">
                {isLimited ? (
                  <span>Wait {secondsRemaining}s before posting again</span>
                ) : (
                  <span>Press Enter to send</span>
                )}
              </div>
              <div className="text-[11px] text-subtext-color">
                {messageContent.length}/500
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-2">
            <p className="text-caption text-subtext-color">
              <button
                onClick={openSignIn}
                className="text-brand-primary hover:underline"
              >
                Sign in
              </button>{' '}
              to join the conversation
            </p>
          </div>
        )}
      </div>

    </div>
  );
}
